import { useCallback, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";

import { DatePicker } from "components/DatePicker";

const INVERTED_RANGE_ERROR = "'To' must be larger than 'From'";

export const DateField = ({ attributeName, onSetField, filters }) => {
  const [rangeData, setRangeData] = useState([null, null]);
  const [rangeError, setRangeError] = useState(["", ""]);

  useEffect(() => {
    const from = filters[attributeName]?.find(({ type_name }) =>
      type_name.includes("__gte")
    );
    const to = filters[attributeName]?.find(({ type_name }) =>
      type_name.includes("__lte")
    );
    setRangeData([from?.name ?? null, to?.name ?? null]);
  }, [attributeName, filters]);

  const handleRangeData = useCallback(
    (value, position) => {
      const unixDate = Math.floor(new Date(value).getTime() / 1000);
      // Bring current range data for comparing
      let newValue = [...rangeData];
      setRangeData(state => {
        newValue = [...state];
        return state;
      });
      const positionIndex = { from: 0, to: 1 };

      // Validation data
      let errorMessages = [...rangeError];
      const isReversed =
        position === "from"
          ? Boolean(newValue[1]) && unixDate > newValue[1]
          : Boolean(newValue[0]) && newValue[0] > unixDate;

      if (
        unixDate &&
        isReversed &&
        (position === "from" ? !!newValue[1] : !!newValue[0])
      ) {
        errorMessages[positionIndex[position]] = INVERTED_RANGE_ERROR;
      } else {
        errorMessages = ["", ""];
      }
      setRangeError([...errorMessages]);

      // Save formatted data
      if (position === "from") {
        newValue[0] = unixDate;
      } else if (position === "to") {
        newValue[1] = unixDate;
      }
      setRangeData(newValue);

      // Update parent data if current data is validated or null value
      if (!isReversed || !unixDate) {
        setRangeData(state => {
          const filterData = state
            .map((el, index) => {
              return {
                type_name: `${attributeName}__${index === 0 ? "gte" : "lte"}`,
                name: el,
                type: "date"
              };
            })
            .filter(el => el.name);
          onSetField(filterData, attributeName);
          return state;
        });
      }
    },
    [rangeData, rangeError, onSetField, attributeName]
  );

  const getDateInputValue = value => {
    return value ? new Date(value * 1000).toLocaleDateString() : "";
  };

  return (
    <RowFields>
      {/* From */}
      <StyledDatePicker
        label={`${attributeName} from`}
        InputProps={{ readOnly: true }}
        value={null}
        error={Boolean(rangeError[0])}
        helperText={rangeError[0]}
        inputValue={getDateInputValue(rangeData[0])}
        onChange={evt => handleRangeData(evt, "from")}
        KeyboardButtonProps={{
          "aria-label": "attribute-date"
        }}
        invalidDateMessage={""}
        inputVariant="outlined"
        margin="dense"
        placeholder={`${attributeName} from`}
        clearable
        onError={() => {}}
      />
      {/* To */}
      <StyledDatePicker
        label={`${attributeName} to`}
        InputProps={{ readOnly: true }}
        value={null}
        error={Boolean(rangeError[1])}
        helperText={rangeError[1]}
        inputValue={getDateInputValue(rangeData[1])}
        onChange={evt => handleRangeData(evt, "to")}
        KeyboardButtonProps={{
          "aria-label": "attribute-date"
        }}
        invalidDateMessage={""}
        inputVariant="outlined"
        margin="dense"
        placeholder={`${attributeName} to`}
        clearable
        onError={() => {}}
      />
    </RowFields>
  );
};

const RowFields = styled("div")`
  display: flex;
  width: 100%;
  gap: 10px;

  & .MuiTextField-root {
    width: 100%;
  }
`;

const StyledDatePicker = styled(DatePicker)`
  .MuiFormLabel-root {
    &:not(.Mui-focused) {
      color: rgba(0, 0, 0, 0.4);
    }
  }
`;
StyledDatePicker.displayName = "StyledAppDatePicker";

DateField.propTypes = {
  attributeName: PropTypes.string.isRequired,
  onSetField: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired
};
